import React, { useEffect, useState } from "react"

import { AdminRoutes } from "@/routes/private"
import { Checkbox, Container, FormControlLabel, Grid, Link, Typography } from "@material-ui/core"

import useStyles from "@/pages/Admin/styles"
import { ActionDialog, Divider, Notification, UpsellDialog } from "@/components"
import Menu from "@/pages/Admin/Menu"
import { useLocation, matchPath } from "react-router"
import useDidMount from "@/hooks/useDidMount"
import { useGlobalStateStore } from "@/store/GlobalState"

import Popups from "@/pages/Admin/Dashboard/Popups"
import useSubscriptionLimits from "@/hooks/useSubscriptionLimits"
import ErrorHandlerService from "@/services/ErrorHandler"
import { ErrorType } from "@/hooks/useValidation"
import ApiService from "@/services/Api"
import { letalkLinks } from "@/utils/link"
import useWABACredits from "@/@integrations/WABA/hooks/useWABACredits"
import HardCoded from "@/services/HardCoded"
import { runAfterReactRender } from "@/utils/node"

type TermsOfServiceOptionsData = {
	isTermsOfServiceChecked: boolean
}

const ROUTES_WITHOUT_CONTAINER_WIDTH_LIMITATION: string[] = [
	"/admin/bot",
	"/admin/bots/:chatBotFlowId",
	"/admin/automation/flow/:chatBotFlowId",
	"/admin/attendance/widgets/:widgetId"
]

const ROUTES_WITH_CUSTOM_CONTAINER: string[] = [
	"/admin/template/manage"
]

const Admin: React.FC = () => {
	const [openDrawer, setOpenDrawer] = useState(false)

	const globalStateStore = useGlobalStateStore()
	const wabaTemplateCreditsQuantityLimit = useSubscriptionLimits("quantity", "waba_template_credits")
	const canUseWABATemplateCreditsLimit = HardCoded.checkFeatureFlag("canUseWABATemplateCreditsLimit")

	const {
		handleOpenTemplateSendLimitNearLimitDialog,
		isWABATemplateCreditsLoading
	} = useWABACredits()

	const userStore = globalStateStore.user

	const isAdminUser = userStore?.extra_data?.is_admin_mode

	const isTrial = globalStateStore?.instance?.subscriptionData?.isTrial

	const classes = useStyles({ openDrawer, isTrial })
	const location = useLocation()

	const isCurrentPath = (path: string) => {
		const matchedPath = matchPath(location.pathname, { path, exact: true, strict: true })

		return Boolean(matchedPath)
	}

	const handleDrawerClose = () => {
		setOpenDrawer(false)
	}

	const [openTermsOfServiceDialog, setOpenTermsOfServiceDialog] = useState(false)
	const [termsOfServiceOptions, setTermsOfServiceOptions] = useState<TermsOfServiceOptionsData>({
		isTermsOfServiceChecked: false
	})

	const handleCloseTermsOfServiceDialog = () => {
		setOpenTermsOfServiceDialog(false)
	}

	const saveTermsOfService = async (instanceId: number) => {
		await ApiService.put(`/instance/${instanceId}/accept-terms-of-use`)
	}

	const handleSaveTermsOfServiceDialog = async () => {
		if (termsOfServiceOptions.isTermsOfServiceChecked) {
			try {
				await saveTermsOfService(globalStateStore.instance.instance_id)
				globalStateStore.setInstanceData({
					is_terms_of_service_accepted: true
				})

				handleCloseTermsOfServiceDialog()
			} catch (error) {
				ErrorHandlerService.handle(error as ErrorType)

				Notification.error({
					message: "Erro ao aceitar Termos de Uso."
				})
				handleCloseTermsOfServiceDialog()
			}
		} else {
			Notification.error({
				message: "Por favor, declare que leu os Termos de Uso"
			})
		}
	}

	const isInstanceOwner = globalStateStore.user.is_instance_owner
	const willShowTermsOfServiceDialog = !globalStateStore.instance.is_terms_of_service_accepted && !isAdminUser

	useEffect(() => {
		setOpenTermsOfServiceDialog(true)
	}, [])

	useEffect(() => {
		handleDrawerClose()
	}, [location.pathname])

	useDidMount(() => {
		window.name = "inbox_admin_page"
	})

	useEffect(() => {
		if (!isWABATemplateCreditsLoading && canUseWABATemplateCreditsLimit) {
			if (wabaTemplateCreditsQuantityLimit.mustBlock) {
				runAfterReactRender(() => UpsellDialog.open({
					dialogCode: wabaTemplateCreditsQuantityLimit.blockCode
				}))
			} else {
				runAfterReactRender(() => handleOpenTemplateSendLimitNearLimitDialog())
			}
		}
	}, [wabaTemplateCreditsQuantityLimit.mustBlock, isWABATemplateCreditsLoading])

	return (
		<Grid
			container
			direction="column"
			wrap="nowrap"
		>
			<Grid
				container
				className={classes.adminContainer}
			>
				<Menu
					openDrawer={openDrawer}
					onClose={handleDrawerClose}
				/>
				<Popups />
				{isInstanceOwner && willShowTermsOfServiceDialog &&
				<ActionDialog
					title="Atualização dos Termos de Uso"
					openDialog={openTermsOfServiceDialog}
					fullWidth={true}
					saveText="EU ACEITO"
					cancelText="LEMBRAR DEPOIS"
					onSave={handleSaveTermsOfServiceDialog}
					onClose={handleCloseTermsOfServiceDialog}
				>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Typography>
								<b>Atualizamos nossos Termos de Uso</b> para oferecer mais transparência, segurança e clareza na utilização da plataforma.
								Como responsável pela instância, é necessário que você leia e aceite os novos termos para garantir a continuidade do acesso completo aos recursos.
							</Typography>
						</Grid>
						<Grid item xs={12}>
							<FormControlLabel
								control={
									<Checkbox
										checked={termsOfServiceOptions.isTermsOfServiceChecked || false}
										onChange={(event) => {
											setTermsOfServiceOptions({
												...termsOfServiceOptions,
												isTermsOfServiceChecked: event.target.checked
											})
										}}
									/>
								}
								label={
									<>
										Declaro que li, compreendi e aceito os <Link
											href={letalkLinks.notionTermsOfService}
											target="_BLANK"
										>
											Termos de Uso
										</Link>.
									</>
								}
							/>
						</Grid>
					</Grid>
				</ActionDialog>
				}

				<Grid
					container
					justify="center"
					className={classes.routesContainer}
				>
					<Container
						className={classes.routesContent}
						{...(ROUTES_WITHOUT_CONTAINER_WIDTH_LIMITATION.some(path => isCurrentPath(path)) ? { sx: 12 } : {})}
						style={{
							...(ROUTES_WITHOUT_CONTAINER_WIDTH_LIMITATION.some(path => isCurrentPath(path)) && ({
								maxWidth: "none"
							})),
							...(ROUTES_WITH_CUSTOM_CONTAINER.some(path => isCurrentPath(path)) && ({
								display: "flex",
								flexDirection: "row",
								position: "relative",
								height: "158%"
							}))
						}}
					>
						{!ROUTES_WITH_CUSTOM_CONTAINER.some(path => isCurrentPath(path)) && <Divider size={4} orientation="horizontal" />}

						<AdminRoutes />

						{!ROUTES_WITH_CUSTOM_CONTAINER.some(path => isCurrentPath(path)) && <Divider size={4} orientation="horizontal" />}
					</Container>
				</Grid>
			</Grid>
		</Grid>
	)
}

export default Admin
